import type {
  ComponentStatus,
  ComponentsResponse,
  MonitoringAlertsSnapshot,
  MonitoringOverview,
  SandboxRuntimeSnapshot,
  TeamRuntimeSnapshot,
} from "../api";
import type { Translator } from "../i18n";

export type Source<T> = {
  data: T | null;
  error: boolean;
  fetchedAt: number | null;
};
export type ExecutionSource =
  | "sandbox"
  | "registrations"
  | "publish"
  | "operations";
export interface ExecutionRecord {
  id: string;
  team_id?: string;
  resource_id?: string;
  kind: string;
  state: string;
  created_at: string;
  updated_at: string;
  completed_at?: string;
  error_code?: string;
  actor?: string;
  reason?: string;
}
export interface ExecutionSnapshot {
  data_center_id: string;
  observed_at: string;
  source: ExecutionSource;
  available: boolean;
  coverage: string;
  items: ExecutionRecord[];
  warnings: string[];
}
export interface MonitoringData {
  telemetry: Source<MonitoringOverview>;
  alerts: Source<MonitoringAlertsSnapshot>;
  components: Source<ComponentsResponse>;
  placement: Source<TeamRuntimeSnapshot>;
  sandbox: Source<SandboxRuntimeSnapshot>;
  changes: Source<ExecutionSnapshot>;
}
export type Health = "healthy" | "degraded" | "critical" | "unknown";
export interface ComponentRow {
  id: string;
  name: string;
  state: Health;
  detail: string;
  capacity: string;
  observedAt?: string;
  href?: string;
}

export function fresh(source: Source<unknown>, now: number): boolean {
  if (
    source.error ||
    !source.data ||
    source.fetchedAt === null ||
    now - source.fetchedAt > 90_000
  )
    return false;
  const date = (source.data as { observed_at?: string }).observed_at;
  if (!date) return false;
  const observed = Date.parse(date);
  return (
    Number.isFinite(observed) &&
    observed <= now + 30_000 &&
    now - observed <= 90_000
  );
}
export function number(value: number | null | undefined, digits = 1): string {
  return value == null || !Number.isFinite(value)
    ? "—"
    : value.toLocaleString(undefined, { maximumFractionDigits: digits });
}
export function date(value?: string | null): string {
  if (!value || !Number.isFinite(Date.parse(value))) return "—";
  return new Date(value).toLocaleString(
    document.documentElement.lang || "zh-CN",
    {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    },
  );
}
export function duration(seconds: number | null | undefined): string {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) return "—";
  return seconds < 1
    ? number(seconds * 1000) + " ms"
    : seconds < 60
      ? number(seconds) + " s"
      : number(seconds / 60) + " min";
}
export function healthLabel(state: Health, t: Translator): string {
  return {
    healthy: t("正常", "Healthy"),
    degraded: t("需关注", "Degraded"),
    critical: t("异常", "Critical"),
    unknown: t("未知", "Unknown"),
  }[state];
}
export function stateLabel(state: string, t: Translator): string {
  const labels: Record<string, [string, string]> = {
    active: ["触发中", "Firing"],
    suppressed: ["已抑制/静默", "Suppressed"],
    pending: ["等待中", "Pending"],
    inactive: ["未触发", "Inactive"],
    succeeded: ["成功", "Succeeded"],
    success: ["成功", "Succeeded"],
    completed: ["完成", "Completed"],
    failed: ["失败", "Failed"],
    timeout: ["超时", "Timed out"],
    timed_out: ["超时", "Timed out"],
    cancelled: ["已取消", "Cancelled"],
    canceled: ["已取消", "Cancelled"],
    running: ["运行中", "Running"],
    requested: ["已请求", "Requested"],
    ready: ["已就绪", "Ready"],
    expired: ["已结束", "Expired"],
    queued: ["排队中", "Queued"],
  };
  return labels[state] ? t(...labels[state]) : state;
}
function health(state: ComponentStatus): Health {
  return state === "unavailable"
    ? "critical"
    : state === "disabled"
      ? "unknown"
      : state;
}
export function components(
  data: MonitoringData,
  now: number,
  t: Translator,
): ComponentRow[] {
  const rows: ComponentRow[] = (data.components.data?.items ?? []).map(
    (item) => ({
      id: item.id,
      name: item.name,
      state: fresh(data.components, now) ? health(item.status) : "unknown",
      detail: item.error_code || item.message || "—",
      observedAt: item.observed_at,
      capacity: "—",
      href: (
        {
          kubernetes: "/cluster",
          ids: "/ids",
          sandbox: "/sandbox",
          "llm-gateway": "/llm-gateway",
          "team-placement": "/team-runtime",
        } as Record<string, string>
      )[item.id],
    }),
  );
  const instances = data.placement.data?.instances ?? [];
  const placementFresh = fresh(data.placement, now);
  const healthyInstances = instances.filter(
    (item) =>
      item.lifecycle === "ready" &&
      now - Date.parse(item.heartbeat_at) <= 90_000,
  );
  const invalidPlacements =
    data.placement.data?.placements.filter((item) => !item.route_eligible) ??
    [];
  const loaded = instances.reduce((v, item) => v + item.loaded_team_count, 0);
  const slots = instances.reduce((v, item) => v + item.max_team_slots, 0);
  rows.push({
    id: "pacture-api",
    name: "Pacture API",
    state: !placementFresh
      ? "unknown"
      : !instances.length || !healthyInstances.length
        ? "critical"
        : healthyInstances.length < instances.length ||
            invalidPlacements.length > 0 ||
            loaded >= slots
          ? "degraded"
          : "healthy",
    detail: t(
      `${healthyInstances.length}/${instances.length} 实例就绪；${invalidPlacements.length} 个 Team 不可路由`,
      `${healthyInstances.length}/${instances.length} instances ready; ${invalidPlacements.length} unroutable Teams`,
    ),
    capacity: placementFresh ? `${loaded} / ${slots} Team slots` : "—",
    observedAt: data.placement.data?.observed_at,
    href: "/team-runtime",
  });
  const metrics = data.telemetry.data?.metrics;
  rows.push({
    id: "telemetry",
    name: t("采集链路", "Telemetry pipeline"),
    state: fresh(data.telemetry, now) ? data.telemetry.data!.status : "unknown",
    detail: t("采集目标与导出队列", "Collector targets and export queue"),
    capacity: fresh(data.telemetry, now)
      ? `${number(metrics?.healthy_collector_targets)} / ${number(metrics?.collector_targets)}`
      : "—",
    observedAt: data.telemetry.data?.observed_at,
  });
  for (const [id, name] of [
    ["kubernetes", "Kubernetes"],
    ["ids", "IDS"],
    ["team-placement", "Team Placement"],
    ["sandbox", "Sandbox"],
    ["llm-gateway", "LiteLLM"],
    ["tidb", "TiDB"],
    ["ingress", "Pacture Ingress"],
    ["storage", t("对象/工作区存储", "Object / workspace storage")],
  ]) {
    if (!rows.some((row) => row.id === id))
      rows.push({
        id,
        name,
        state: "unknown",
        detail: t("健康数据未接入", "Health source not connected"),
        capacity: "—",
      });
  }
  return rows;
}
export function reportHealth(
  rows: ComponentRow[],
  alerts: Source<MonitoringAlertsSnapshot>,
  now: number,
): Health {
  if (
    rows.some((row) => row.state === "critical") ||
    (fresh(alerts, now) &&
      alerts.data?.items.some(
        (a) => a.state === "active" && a.labels.severity === "critical",
      ))
  )
    return "critical";
  if (
    rows.some((row) => row.state === "degraded") ||
    (fresh(alerts, now) && alerts.data?.items.some((a) => a.state === "active"))
  )
    return "degraded";
  if (
    !fresh(alerts, now) ||
    !alerts.data?.alerts_available ||
    rows.some((row) => row.state === "unknown")
  )
    return "unknown";
  return rows.length ? "healthy" : "unknown";
}
